import { PAGE_SIZE } from '@constants/api-constants';
import { PrivateURLConstants } from '@constants/private-url.constant';
import { Page, EnvironmentResult } from '@testsigmainc/models';

import { useInfiniteQuery } from 'react-query';
import api from '../../../api/instance';
import { FilterDropdown } from '../models/filter-dropdown.model';

const nameSpace = PrivateURLConstants.ENVIRONMENT_RESULTS;

const findAll = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
  args?: {
    [key: string]: any;
  },
): Promise<Page<EnvironmentResult>> => {
  return api
    .get(nameSpace, { query, sort, size, page, ...(args || {}) })
    .then((res) =>
      new Page<EnvironmentResult>().deserialize(res, EnvironmentResult),
    )
    .catch((e) => Promise.reject(e));
};

const findAllPenultimate = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
): Page<EnvironmentResult> => {
  return api
    .get(nameSpace + '/penultimate_results', { query, sort, size, page })
    .then((res) =>
      new Page<EnvironmentResult>().deserialize(res, EnvironmentResult),
    )
    .catch((e) => Promise.reject(e));
};

const findAllTestMachinesList = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
  args?: {
    [key: string]: any;
  },
): Page<FilterDropdown> => {
  return api
    .get(`${nameSpace}/list`, { query, sort, size, page, ...(args || {}) })
    .then((res) => new Page<FilterDropdown>().deserialize(res, FilterDropdown))
    .catch((e) => Promise.reject(e));
};
const findById = (envResultId: number) => {
  return api
    .get(nameSpace + '/' + envResultId)
    .then((res) => new EnvironmentResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const assetSync = (envResultId: number) => {
  const url = nameSpace + '/' + envResultId + '/assets/sync';

  return api
    .get(url)
    .then()
    .catch((e) => Promise.reject(e));
};

const assetStatus = (envResultId: number): Promise<string> => {
  const url = nameSpace + '/' + envResultId + '/assets/status';

  return api
    .get(url)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

export const useFindAllEnvironments = ({
  query,
  enabled,
  args = {},
  refetchOnMount,
  queryCacheKey = [],
  callback = () => {},
}) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [...queryCacheKey, `machine-result-infinite-${query}`].filter(
      Boolean,
    ),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await findAll(query, null, PAGE_SIZE, pageParam, args);
      if (callback) {
        callback(response?.totalElements);
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1;
    },
    enabled,
    refetchOnMount,
  });
};

export const useFindAllTestMachines = ({
  query,
  enabled,
  args = {},
  refetchOnMount,
  queryCacheKey = '',
  callback = () => {},
}) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [queryCacheKey, `machine-result-infinite-${query}`].filter(
      Boolean,
    ),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await findAllTestMachinesList(
        query,
        null,
        PAGE_SIZE,
        pageParam,
        args,
      );
      if (callback) {
        callback(response?.totalElements);
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1;
    },
    enabled,
    refetchOnMount,
  });
};
export const EnvironmentResultService = {
  findAll,
  findAllPenultimate,
  findById,
  assetSync,
  assetStatus,
};
